/**
 * apps/automations-portal/src/features/admin/DeploymentCard.tsx
 *
 * A single deployment's card on ClientDetailPage, plus the actions the admin
 * portal can now take on it: enable/disable, edit configuration, test run,
 * view recent runs, and delete. Split out of ClientDetailPage so each of
 * these can be reasoned about (and tested) on their own.
 *
 * All API calls here call back up to the parent via `onRefetch` rather than
 * holding their own copy of the deployment — the client detail response is
 * the single source of truth, so every action re-reads it once the call
 * settles instead of guessing at the new state itself.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, Play, Trash2 } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardContent, CardHeader, CardTitle, Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';
import { ApiError, adminApi } from '@/lib/api';
import type { Block, Deployment, DeploymentStatus } from '@/lib/types';
import { ConfigForm, toConfigObject, type ConfigValue } from './ConfigForm';

const DEPLOYMENT_STATUS_VARIANT: Record<DeploymentStatus, BadgeProps['variant']> = {
  ENABLED: 'success',
  DISABLED: 'secondary',
  DRAFT: 'secondary',
  DEPLOYING: 'warning',
  ERROR: 'destructive',
};

/** Plain-English labels — the raw database values are not for reading. */
const DEPLOYMENT_STATUS_LABEL: Record<DeploymentStatus, string> = {
  ENABLED: 'Live',
  DISABLED: 'Off',
  DRAFT: 'Draft',
  DEPLOYING: 'Deploying',
  ERROR: 'Error',
};

/**
 * Verbatim sample payloads per block, so a test run has something sensible
 * prefilled rather than an empty `{}`. A blockId outside this set (a new
 * block the portal doesn't know a sample for yet) falls back to `{}`.
 */
const SAMPLE_TEST_RUN_PAYLOADS: Record<string, Record<string, unknown>> = {
  'invoice-chaser': {
    invoice_number: 'INV-2043',
    amount: '1240.00',
    due_date: '2026-07-01',
    customer_name: 'Priya Marchant',
    customer_email: 'priya@example.com',
  },
  'enquiry-answerer': {
    from_name: 'Sam Whitfield',
    from_email: 'sam@example.com',
    subject: 'Bathroom quote',
    body: 'Hi, could you quote for a bathroom refit? Thanks, Sam',
  },
  'document-intake': {
    document_text: 'WESTGATE MERCHANTS\nInvoice WGB-118204\nTotal 224.52',
    source: 'manual test',
  },
  'quote-builder': {
    job_description: 'Replace back boiler with a combi, 6m pipe run',
    customer_name: 'Mrs Okonkwo',
    customer_email: 'o@example.com',
  },
  'review-requester': {
    customer_name: 'Ellie Fenwick',
    customer_email: 'ellie@example.com',
    job_ref: 'outside tap',
    immediate: true,
  },
};

function samplePayloadFor(blockId: string): Record<string, unknown> {
  return SAMPLE_TEST_RUN_PAYLOADS[blockId] ?? {};
}

export interface DeploymentCardProps {
  deployment: Deployment;
  /** The block this deployment configures — undefined if the catalogue hasn't loaded or no longer has it. */
  block: Block | undefined;
  /** Reload the client (and so this deployment) from the server. */
  onRefetch: () => void;
  /** The existing missingInEngine "Remove" behaviour — deletes without a confirmation step, since the flow is already gone. */
  onRemoveMissing: () => void;
  /** A full delete, gated behind the confirmation dialog below. */
  onDelete: () => Promise<void>;
}

export function DeploymentCard({ deployment, block, onRefetch, onRemoveMissing, onDelete }: DeploymentCardProps) {
  const [enabled, setEnabled] = useState(deployment.status === 'ENABLED');
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The parent re-fetches after every action, so once that lands the badge
  // and the toggle should agree again rather than keep showing the
  // optimistic guess.
  useEffect(() => {
    setEnabled(deployment.status === 'ENABLED');
  }, [deployment.status]);

  async function handleToggle(next: boolean) {
    setEnabled(next);
    setTogglingEnabled(true);
    try {
      await adminApi.setDeploymentEnabled(deployment.id, next);
      toast(next ? 'Enabled' : 'Disabled', {
        description: `${deployment.blockName} is now ${next ? 'live' : 'switched off'}.`,
      });
    } catch (err) {
      toast('Could not update', {
        description: err instanceof ApiError ? err.message : 'Something went wrong. Try again.',
      });
    } finally {
      setTogglingEnabled(false);
      onRefetch();
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{deployment.blockName}</CardTitle>
          {deployment.seededDemo ? (
            <Badge variant="secondary">Demo data</Badge>
          ) : deployment.missingInEngine ? (
            <Badge variant="destructive">Removed</Badge>
          ) : (
            <Badge variant={DEPLOYMENT_STATUS_VARIANT[deployment.status] ?? 'secondary'}>
              {DEPLOYMENT_STATUS_LABEL[deployment.status] ?? deployment.status}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <p className="text-xs text-muted-foreground">
          Deployed {new Date(deployment.createdAt).toLocaleDateString('en-GB')}
        </p>
        {deployment.missingInEngine && !deployment.seededDemo && (
          <div className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2">
            <p className="text-xs text-destructive">
              This automation no longer exists in the engine — it looks like it was
              deleted there by hand. Remove it here, then deploy the block again.
            </p>
            <Button type="button" variant="outline" size="sm" className="w-fit" onClick={onRemoveMissing}>
              Remove
            </Button>
          </div>
        )}
        {deployment.seededDemo && (
          <p className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">
            Seeded for demonstration. This block is not running in the engine, so it
            cannot be switched, edited or test-run. Deploy the block for real to do
            any of that.
          </p>
        )}

        {deployment.statusDetail && !deployment.missingInEngine && !deployment.seededDemo && (
          <p className="text-xs text-destructive">{deployment.statusDetail}</p>
        )}

        {!deployment.missingInEngine && !deployment.seededDemo && (
          <>
            <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={enabled}
                  onCheckedChange={(next) => void handleToggle(next)}
                  disabled={togglingEnabled}
                  aria-label={`${enabled ? 'Disable' : 'Enable'} ${deployment.blockName}`}
                />
                <span className="text-xs text-muted-foreground">
                  {togglingEnabled ? 'Updating…' : enabled ? 'Live' : 'Off'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Edit configuration for ${deployment.blockName}`}
                  onClick={() => setEditOpen(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Test run ${deployment.blockName}`}
                  onClick={() => setTestRunOpen(true)}
                >
                  <Play className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${deployment.blockName}`}
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <RecentRunsSection deploymentId={deployment.id} />

            <EditConfigDialog
              deployment={deployment}
              block={block}
              open={editOpen}
              onOpenChange={setEditOpen}
              onSaved={onRefetch}
            />
            <TestRunDialog deployment={deployment} open={testRunOpen} onOpenChange={setTestRunOpen} />
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete {deployment.blockName}?</DialogTitle>
                  <DialogDescription>
                    This removes the automation from the automation engine as well as this client&apos;s record.
                    It cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button type="button" variant="destructive" onClick={() => void handleConfirmDelete()} disabled={deleting}>
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {deleting ? 'Deleting…' : 'Delete'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function EditConfigDialog({
  deployment,
  block,
  open,
  onOpenChange,
  onSaved,
}: {
  deployment: Deployment;
  block: Block | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState<ConfigValue>({});
  const [serverIssues, setServerIssues] = useState<{ key: string; message: string }[]>([]);
  const [valid, setValid] = useState(true);
  const [saving, setSaving] = useState(false);

  const fields = block?.fields ?? [];

  useEffect(() => {
    if (!open) return;
    setValue(deployment.config ?? {});
    setServerIssues([]);
  }, [open, deployment]);

  async function handleSave() {
    setSaving(true);
    try {
      await adminApi.updateDeploymentConfig(deployment.id, toConfigObject(fields, value));
      toast('Configuration saved', { description: `${deployment.blockName} has been updated.` });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.issues?.length) {
        // Same mechanism as the deploy wizard: send the admin back to the
        // fields the server rejected instead of leaving one opaque toast.
        setServerIssues(err.issues);
        toast('Check the highlighted fields', { description: err.message });
      } else if (err instanceof ApiError) {
        toast('Could not save', { description: err.message });
      } else {
        toast('Could not save', { description: 'Something went wrong. Try again.' });
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit configuration</DialogTitle>
          <DialogDescription>Update {deployment.blockName} for this client.</DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-y-auto pr-1">
          {fields.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This automation&apos;s configuration schema is not available right now.
            </p>
          ) : (
            <ConfigForm
              fields={fields}
              value={value}
              serverIssues={serverIssues}
              onChange={(next) => {
                setServerIssues([]);
                setValue(next);
              }}
              onValidChange={setValid}
            />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !valid || fields.length === 0}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestRunDialog({
  deployment,
  open,
  onOpenChange,
}: {
  deployment: Deployment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [text, setText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [runIssue, setRunIssue] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(JSON.stringify(samplePayloadFor(deployment.blockId), null, 2));
    setParseError(null);
    setRunIssue(null);
  }, [open, deployment.blockId]);

  async function handleRun() {
    let payload: Record<string, unknown>;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setParseError('Enter a JSON object.');
        return;
      }
      payload = parsed as Record<string, unknown>;
    } catch {
      setParseError('That is not valid JSON.');
      return;
    }

    setParseError(null);
    setRunIssue(null);
    setRunning(true);
    try {
      await adminApi.testRunDeployment(deployment.id, payload);
      toast('Test run started', { description: `${deployment.blockName} is running now.` });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DEPLOYMENT_DISABLED') {
        setRunIssue('This automation is currently switched off. Enable it, then run the test again.');
        toast('Enable the deployment first', {
          description: 'Test runs need the automation switched on.',
        });
      } else if (err instanceof ApiError) {
        toast('Test run failed', { description: err.message });
      } else {
        toast('Test run failed', { description: 'Something went wrong. Try again.' });
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Test run {deployment.blockName}</DialogTitle>
          <DialogDescription>Send a sample payload through this automation.</DialogDescription>
        </DialogHeader>
        <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
          This runs the automation for real. While test mode is on, emails go to you rather than the customer.
        </p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`test-run-payload-${deployment.id}`}>Payload (JSON)</Label>
          <Textarea
            id={`test-run-payload-${deployment.id}`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setParseError(null);
            }}
            rows={10}
            className="font-mono text-xs"
          />
          {parseError && <p className="text-sm text-destructive">{parseError}</p>}
          {runIssue && <p className="text-sm text-destructive">{runIssue}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleRun()} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {running ? 'Running…' : 'Run test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RunEntry {
  id?: string | undefined;
  status?: string | undefined;
  startTime?: string | undefined;
  finishTime?: string | undefined;
}

/** deploymentRuns' shape is loose — treat every entry defensively rather than trusting all fields are present. */
function toRunEntry(raw: unknown): RunEntry {
  if (typeof raw !== 'object' || raw === null) return {};
  const r = raw as Record<string, unknown>;
  return {
    id: typeof r.id === 'string' ? r.id : undefined,
    status: typeof r.status === 'string' ? r.status : undefined,
    startTime: typeof r.startTime === 'string' ? r.startTime : undefined,
    finishTime: typeof r.finishTime === 'string' ? r.finishTime : undefined,
  };
}

function RecentRunsSection({ deploymentId }: { deploymentId: string }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [runs, setRuns] = useState<RunEntry[]>([]);

  function load() {
    setState('loading');
    adminApi
      .deploymentRuns(deploymentId)
      .then((data) => {
        setRuns(Array.isArray(data) ? data.map(toRunEntry) : []);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && state === 'idle') load();
  }

  return (
    <div className="border-t border-border pt-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        Recent runs
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-2">
          {state === 'loading' && <Skeleton className="h-16 w-full" />}
          {state === 'error' && <p className="text-xs text-destructive">Could not load recent runs.</p>}
          {state === 'ready' && runs.length === 0 && (
            <p className="text-xs text-muted-foreground">No runs yet.</p>
          )}
          {state === 'ready' && runs.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run, index) => (
                  <TableRow key={run.id ?? index}>
                    <TableCell className="p-2 text-xs">{run.status ?? 'Unknown'}</TableCell>
                    <TableCell className="p-2 text-xs">
                      {run.startTime ? new Date(run.startTime).toLocaleString('en-GB') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}
